import React from "react";

import { Grid, Skeleton } from "@mui/material";
import { StylesProvider, createGenerateClassName } from "@mui/styles";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { useCustomerPortalJobContext } from "Context/CustomerPortalJobContext";
import { useActionJobDocument } from "Hooks/useActionJobDocument";
import { useLoadingIds } from "Hooks/useLoadingIds";
import { JobDocument } from "Models/job.document";
import { useTranslation } from "react-i18next";
import { DocumentCustomerPortal } from "Util/enums";

import { CustomerPortalCard } from "../../CustomerPortalCard";
import { ItemDocument } from "./ItemDocument";
import { LoadingContainer, LoadingSubTitle, LoadingTitle, LoadingWrapper } from "./styled";

const EmptyDocuments = require("Assets/img/empty-documents.svg");

const generateClassName = createGenerateClassName({
  productionPrefix: "cp-2-",
  seed: "customer-portal-2",
});

const documentTypesThatNeedsShipment = ["Bill of Lading"];

const getStatus = (jobDocument: JobDocument, needShipment: boolean) => {
  if (jobDocument.uploadedAt && jobDocument.uploaded) return DocumentCustomerPortal.UPLOADED;

  if (!jobDocument.isRequested) return DocumentCustomerPortal.CANCELLED;

  if (documentTypesThatNeedsShipment.includes(jobDocument.name) && needShipment) {
    return DocumentCustomerPortal.MISSING_SHIPMENT;
  }

  return DocumentCustomerPortal.MISSING;
};

const DocumentsPanel = () => {
  const { order, customerShipments, documents, refetchDocuments, isInitialLoadingDocuments } =
    useCustomerPortalJobContext();

  if (!order?.id) return <div></div>;

  const { t: translate } = useTranslation();
  const { useUploadDocument } = useActionJobDocument();

  const { setLoadingId, unsetLoadingId, getLoadingStatusById } = useLoadingIds();
  const isEmpty = !isInitialLoadingDocuments && !documents?.length;

  const { mutate: requestUploadDocument } = useUploadDocument({
    onRequest(jobDocument) {
      setLoadingId(jobDocument.id);
    },
    onUploadSuccess: async (_, jobDocument) => {
      await refetchDocuments();
      NotificationToast.success(translate("documentsUploadedSuccessfully"));
      unsetLoadingId(jobDocument.id);
    },
    onUploadError(_error, jobDocument, _) {
      NotificationToast.error(translate("anErrorOccurredOnTryAgain"));
      unsetLoadingId(jobDocument.id);
    },
  });

  const handleUploadDocument = (
    document: JobDocument,
    uri: string,
    fileName: string,
    fileExtension: string
  ) => {
    const payload = {
      ...document,
      filename: fileName,
      file: uri,
      fileType: fileExtension || "pdf",
    };

    requestUploadDocument(payload);
  };

  const renderEmptyState = () => {
    return (
      <LoadingWrapper>
        <LoadingContainer>
          <img src={EmptyDocuments} alt="Empty documents" height={160} />

          <LoadingTitle>{translate("noDocumentsRequestedYet")}</LoadingTitle>

          <LoadingSubTitle>
            {translate(
              "donTWorryIfAnyDocumentsAreRequestedByUsYouWillBeNotifiedAndWillBeAbleToManageThem"
            )}
          </LoadingSubTitle>
        </LoadingContainer>
      </LoadingWrapper>
    );
  };

  const renderGridItems = () => {
    if (isInitialLoadingDocuments)
      return Array.from({ length: 2 }).map((_value, index) => {
        return (
          <Grid item xs={12} md={6} key={index}>
            <Skeleton variant="rounded" height={104} />
          </Grid>
        );
      });

    return documents?.map((document) => {
      const status = getStatus(document, !customerShipments?.length);
      const isLoading = getLoadingStatusById(document.id);

      return (
        <Grid item xs={12} md={6} key={document.id}>
          <ItemDocument
            title={document.name}
            status={status}
            url={document.file}
            uploadDate={document.uploadedAt}
            dueDate={document.dueDate}
            onUploadDocument={(uri, fileName, fileExtension) =>
              handleUploadDocument(document, uri, fileName, fileExtension)
            }
            isLoading={isLoading}
          />
        </Grid>
      );
    });
  };

  const renderContent = () => {
    if (isEmpty) return renderEmptyState();

    return (
      <Grid container justifyContent="space-between" spacing={3} alignItems="stretch">
        {renderGridItems()}
      </Grid>
    );
  };

  return (
    <StylesProvider generateClassName={generateClassName}>
      <CustomerPortalCard title={!isEmpty && translate("documents")} block fullHeight>
        {renderContent()}
      </CustomerPortalCard>
    </StylesProvider>
  );
};

export { DocumentsPanel };
